import { useEffect, useRef, useState } from "react";
import { Send, UserCheck, CheckCircle2, Bot, MessageCircle, CalendarCheck, CalendarPlus, Paperclip, X, FileText, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import VoiceRecorder from "@/components/conversations/VoiceRecorder";
import TemplateDialog from "@/components/conversations/TemplateDialog";
import BookingLinkDialog from "@/components/conversations/BookingLinkDialog";
import ConversationClassification from "@/components/conversations/ConversationClassification";
import MessageFeedback from "@/components/conversations/MessageFeedback";
import SmsComposer from "@/components/conversations/SmsComposer";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { CONVERSATIONS } from "@/constants/testIds";
import { CONVERSATION_STATUS_LABELS, CONVERSATION_STATUS_COLORS, formatConversationTime, conversationTitle, conversationContact } from "@/lib/conversationsConstants";
import { MessageMedia, LinkifiedText, DeliveryStatus, deliveryErrorText, AdReferral } from "@/components/conversations/MessageContent";

const SENDER_LABELS = { agent: "Agente IA", staff: "Equipo", system: "Sistema" };

// Fix AI (2026-10-23): en el celular "Enter" hace un renglon nuevo y se manda con el
// boton (el teclado del celular no tiene Shift+Enter): evita mandar un mensaje por error.
function isTouchDevice() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
}

function MessageBubble({ message }) {
  const isInbound = message.direction === "inbound";
  // Fix M (2026-09-30): si el mensaje trae un archivo se muestra el archivo y
  // su texto; si el archivo no se pudo bajar, el texto descriptivo.
  const hasFile = Boolean(message.media && message.media.url);
  const text = hasFile ? message.media.caption : message.body;
  return (
    <div className={`flex ${isInbound ? "justify-start" : "justify-end"}`} data-testid={`${CONVERSATIONS.messageBubblePrefix}${message.id}`}>
      {/* Fix AO (2026-10-23): el pulgar para calificar va debajo de la burbuja */}
      <div className={`max-w-[85%] sm:max-w-[75%] flex flex-col ${isInbound ? "items-start" : "items-end"}`}>
      <div className={`rounded-2xl px-4 py-2.5 ${isInbound ? "bg-muted text-foreground" : "bg-brand text-white"}`}>
        {!isInbound && message.sender_type !== "agent" && (
          <p className="text-[10px] uppercase tracking-wide opacity-80 mb-0.5">{SENDER_LABELS[message.sender_type] || ""}{message.staff_name ? ` · ${message.staff_name}` : ""}{message.template ? " · Plantilla" : ""}</p>
        )}
        <AdReferral referral={message.referral} />
        <MessageMedia media={message.media} />
        {text && <p className="text-sm whitespace-pre-wrap break-words"><LinkifiedText text={text} /></p>}
        {message.delivery_status === "failed" && (
          <p className="text-[11px] mt-1 font-medium">{deliveryErrorText(message.delivery_error)}</p>
        )}
        <p className={`text-[10px] mt-1 ${isInbound ? "text-muted-foreground" : "text-white/70"}`}>
          {message.channel === "sms" && <span className="font-medium" data-testid={`${CONVERSATIONS.smsTagPrefix}${message.id}`}>SMS · </span>}{/* Fix AP (2026-10-23) */}
          {formatConversationTime(message.created_at)}
          {!isInbound && <DeliveryStatus status={message.delivery_status} />}
        </p>
      </div>
      <MessageFeedback message={message} />
      </div>
    </div>
  );
}

/** Detalle de una conversacion: historial completo + acciones de
 * escalado/resolucion + caja de respuesta manual (habilitada solo cuando
 * esta escalada, salvo que el staff decida tomar control manualmente). */
export default function ConversationDetail({ conversation, onSendReply, onSendMedia, onSendTemplate, onChangeStatus, agentGlobalOn, onToggleAgent, services = [], onClassify, tagList, stageList, onBack, smsEnabled = false, onSendSms }) {
  // Fix AI (2026-10-23): con onBack el chat se ve a pantalla completa en el celular (boton para volver a la lista)
  const compact = Boolean(onBack);
  const [draft, setDraftText] = useState("");
  // Fix O (2026-09-30): ventana para mandar una plantilla.
  const [templateOpen, setTemplateOpen] = useState(false);
  // Fix AS (2026-10-27): link de reserva armado por el equipo
  const [bookingLinkOpen, setBookingLinkOpen] = useState(false);
  const [sending, setSending] = useState(false);
  // Fix N (2026-09-30): archivo adjunto pendiente de envio y un borrador por
  // conversacion (al cambiar de chat el texto no pasa al de otro cliente).
  const [pendingFile, setPendingFile] = useState(null);
  const draftsRef = useRef({});
  // Fix AP (2026-10-23): canal elegido en cada chat (WhatsApp o SMS)
  const [channels, setChannels] = useState({});
  const fileInputRef = useRef(null);
  const { toast } = useToast();
  const conversationId = conversation?.id;
  useEffect(() => {
    setPendingFile(null);
    setDraftText(draftsRef.current[conversationId] || "");
  }, [conversationId]);
  const setDraft = (value) => {
    draftsRef.current[conversationId] = value;
    setDraftText(value);
  };

  // Fix AI (2026-10-23): el chat abre en el ultimo mensaje y, si se estaba leyendo lo
  // ultimo, baja solo cuando entra uno nuevo (antes quedaba arriba, en el mas viejo).
  const messagesRef = useRef(null);
  const scrollMemo = useRef({ id: null, count: 0, height: 0 });
  const messageCount = conversation?.messages?.length || 0;
  useEffect(() => {
    const el = messagesRef.current;
    if (!el || !conversationId) return undefined;
    const prev = scrollMemo.current;
    const opened = prev.id !== conversationId;
    const wasNearEnd = prev.height - el.scrollTop - el.clientHeight < 160;
    scrollMemo.current = { id: conversationId, count: messageCount, height: el.scrollHeight };
    if (!opened && !(messageCount > prev.count && wasNearEnd)) return undefined;
    el.scrollTop = el.scrollHeight;
    if (!opened) return undefined;
    const t = setTimeout(() => { el.scrollTop = el.scrollHeight; }, 350);
    return () => clearTimeout(t);
  }, [conversationId, messageCount]);

  if (!conversation) {
    if (compact) {
      // Fix AI (2026-10-23): mientras carga el chat en el celular ya se ve el boton para volver
      return (
        <div className="flex-1 min-h-0 flex flex-col bg-white border border-border rounded-xl overflow-hidden" data-testid={CONVERSATIONS.detailEmptyState}>
          <div className="px-2 py-2 border-b border-border">
            <Button variant="ghost" className="gap-1.5 h-10" onClick={onBack} data-testid={CONVERSATIONS.backButton}><ArrowLeft className="w-5 h-5" /> Chats</Button>
          </div>
          <p className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Cargando la conversación...</p>
        </div>
      );
    }
    return (
      <div className="flex-1 flex items-center justify-center bg-white border border-border rounded-xl" data-testid={CONVERSATIONS.detailEmptyState}>
        <div className="text-center">
          <MessageCircle className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Elegi una conversacion para ver el historial.</p>
        </div>
      </div>
    );
  }

  const handleSend = async () => {
    const text = draft.trim();
    if (!text && !pendingFile) return;
    setSending(true);
    try {
      let ok;
      if (pendingFile) {
        // WhatsApp no admite texto junto a un audio: en ese caso va aparte.
        const isAudio = (pendingFile.type || "").startsWith("audio/");
        ok = await onSendMedia(pendingFile, isAudio ? "" : text, false);
        if (ok) setPendingFile(null);
        if (ok && isAudio && text) ok = await onSendReply(text);
      } else {
        ok = await onSendReply(text);
      }
      if (ok) setDraft("");
    } finally {
      setSending(false);
    }
  };

  // Fix AP (2026-10-23): respuesta por SMS (solo texto)
  const handleSendSmsText = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    try {
      if (await onSendSms(text)) setDraft("");
    } finally {
      setSending(false);
    }
  };

  // Fix AS: el texto con el link se agrega al borrador (despues de lo que ya habia escrito) para que se revise antes de mandar
  const handleInsertBookingLink = (text) => {
    setDraft(draft.trim() ? `${draft.trimEnd()}\n\n${text}` : text);
    toast({ title: "Link agregado al mensaje", description: "Revisalo y mandalo cuando quieras." });
  };

  const handleVoice = async (file) => {
    setSending(true);
    try {
      await onSendMedia(file, "", true);
    } finally {
      setSending(false);
    }
  };

  const handlePickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setPendingFile(file);
  };

  const isEscalated = conversation.status === "escalated";
  const isBot = conversation.status === "bot";
  const bookingLinkButton = (
    <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => setBookingLinkOpen(true)} disabled={sending} title="Enviar link de reserva" aria-label="Enviar link de reserva" data-testid={CONVERSATIONS.bookingLinkButton}>
      <CalendarPlus className="w-4 h-4" />
    </Button>
  );
  const replyEnabled = isEscalated;
  // Fix Q (2026-10-01): "Agente en este chat". Si nunca se toco en este chat,
  // sigue al interruptor general.
  const chatAgentOn = typeof conversation.agent_enabled === "boolean" ? conversation.agent_enabled : Boolean(agentGlobalOn);
  // Fix N: pasadas 24 h del ultimo mensaje del cliente solo se puede mandar una plantilla.
  const windowOpen = Boolean(conversation.window_expires_at) && new Date(conversation.window_expires_at) > new Date();
  // Fix AP (2026-10-23): si los SMS estan prendidos, se elige por donde responder; por defecto, el canal del ultimo mensaje del cliente.
  const lastInbound = [...(conversation.messages || [])].reverse().find((m) => m.direction === "inbound");
  const channel = smsEnabled && replyEnabled ? (channels[conversationId] || (lastInbound?.channel === "sms" ? "sms" : "whatsapp")) : "whatsapp";

  return (
    <div className="group flex-1 min-h-0 min-w-0 flex flex-col bg-white border border-border rounded-xl overflow-hidden">
      {/* Fix AI (2026-10-23): con el celular acostado (poco alto) la cabecera va en una sola linea para dejar lugar a los mensajes */}
      <div className={`${compact ? "px-3 py-2.5 shrink-0 [@media(max-height:499px)]:flex-nowrap" : "px-5 py-4"} border-b border-border flex items-center justify-between flex-wrap gap-2`} data-testid={CONVERSATIONS.detailHeader}>
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <Button variant="ghost" size="icon" className="shrink-0 h-10 w-10 -ml-1" onClick={onBack} aria-label="Volver a los chats" data-testid={CONVERSATIONS.backButton}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{conversationTitle(conversation)}</p>
            <p className="text-xs text-muted-foreground truncate">{conversationContact(conversation)}</p>
          </div>
        </div>
        <div className={`flex flex-wrap items-center gap-2 ${compact ? "[@media(max-height:499px)]:flex-nowrap [@media(max-height:499px)]:shrink-0" : ""}`}>
          <Badge className={`${CONVERSATION_STATUS_COLORS[conversation.status]} border-none font-normal`} data-testid={CONVERSATIONS.detailStatusBadge}>
            {CONVERSATION_STATUS_LABELS[conversation.status]}
          </Badge>
          {/* Fix Q (2026-10-01): interruptor del agente en este chat (reemplaza "Reactivar bot"). */}
          <div className={`flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-md px-2.5 ${compact ? "py-2" : "py-1.5"}`}>
            <Bot className="w-3.5 h-3.5" /> Agente en este chat
            <Switch checked={!isEscalated && chatAgentOn} onCheckedChange={onToggleAgent} data-testid={CONVERSATIONS.agentChatSwitch} />
          </div>
          {(isBot || conversation.status === "resolved") && (
            <Button size="sm" variant="outline" className={`gap-1.5 ${compact ? "h-10" : ""}`} onClick={() => onChangeStatus("escalated")} data-testid={CONVERSATIONS.takeOverButton}>
              <UserCheck className="w-3.5 h-3.5" /> Tomar control
            </Button>
          )}
          {isEscalated && (
            <Button size="sm" variant="outline" className={`gap-1.5 ${compact ? "h-10" : ""}`} onClick={() => onChangeStatus("resolved")} data-testid={CONVERSATIONS.resolveButton}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Resolver
            </Button>
          )}
        </div>
      </div>
      {/* Fix AD (2026-10-15): etapa, servicio de interes y etiquetas del chat. */}
      <ConversationClassification conversation={conversation} services={services} onClassify={onClassify} tagList={tagList} stageList={stageList} compact={compact} />

      <div ref={messagesRef} className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${compact ? "p-3" : "p-5"} space-y-3`} data-testid={CONVERSATIONS.detailMessages}>
        {conversation.booked_appointment && (
          <div className="flex items-center gap-2 bg-[#E3EDE1] text-[#5B7A54] text-xs font-medium px-3 py-2 rounded-lg" data-testid={CONVERSATIONS.bookedAppointmentBadge}>
            <CalendarCheck className="w-3.5 h-3.5 shrink-0" />
            Turno agendado: {conversation.booked_appointment.service_name} con {conversation.booked_appointment.professional_name} · {new Date(conversation.booked_appointment.start_time).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
        {conversation.messages.map((m) => <MessageBubble key={m.id} message={m} />)}
      </div>

      <div className={`${compact ? "p-2.5" : "p-4"} border-t border-border`}>
        {replyEnabled && smsEnabled && (
          <div className="flex items-center gap-1.5 mb-2 text-xs" data-testid={CONVERSATIONS.channelSwitch}>
            <span className="text-muted-foreground mr-1">Responder por</span>
            {[["whatsapp", "WhatsApp", CONVERSATIONS.channelWhatsapp], ["sms", "SMS", CONVERSATIONS.channelSms]].map(([key, label, testId]) => (
              <button
                key={key}
                type="button"
                aria-pressed={channel === key}
                onClick={() => setChannels((prev) => ({ ...prev, [conversationId]: key }))}
                className={`rounded-full border px-3 py-1 [@media(pointer:coarse)]:px-4 [@media(pointer:coarse)]:min-h-[44px] ${channel === key ? "bg-brand text-white border-brand" : "border-border text-muted-foreground hover:bg-muted"}`}
                data-testid={testId}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {replyEnabled && channel === "sms" ? (
          <SmsComposer draft={draft} setDraft={setDraft} sending={sending} onSend={handleSendSmsText} optedOut={Boolean(conversation.sms_opt_out)} compact={compact} isTouch={isTouchDevice()} leading={bookingLinkButton} />
        ) : replyEnabled && !windowOpen ? (
          <div className="flex flex-col items-center gap-2" data-testid={CONVERSATIONS.windowClosedNotice}>
            <p className="text-xs text-muted-foreground text-center">
              {conversation.window_expires_at
                ? "Pasaron mas de 24 horas desde el ultimo mensaje del cliente. WhatsApp solo permite escribirle con una plantilla aprobada."
                : "El cliente todavia no respondio. Hasta que responda, WhatsApp solo permite mandarle plantillas aprobadas."}
            </p>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setTemplateOpen(true)} data-testid={CONVERSATIONS.templateButton}>
              <FileText className="w-3.5 h-3.5" /> Enviar plantilla
            </Button>
          </div>
        ) : replyEnabled ? (
          <div className="space-y-2">
            {pendingFile && (
              <div className="flex items-center gap-2 text-xs bg-muted rounded-lg px-3 py-2" data-testid={CONVERSATIONS.pendingFile}>
                <Paperclip className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate flex-1">{pendingFile.name}</span>
                <button type="button" onClick={() => setPendingFile(null)} className="text-muted-foreground hover:text-foreground" aria-label="Quitar archivo">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className={`flex items-end gap-2 ${compact ? "[&>button]:h-11 [&>button]:min-w-11" : ""}`}>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handlePickFile} data-testid={CONVERSATIONS.fileInput} />
              <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => fileInputRef.current?.click()} disabled={sending} title="Adjuntar archivo" data-testid={CONVERSATIONS.attachButton}>
                <Paperclip className="w-4 h-4" />
              </Button>
              {bookingLinkButton}
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={pendingFile ? "Agrega un texto (opcional)..." : "Escribi tu respuesta..."}
                rows={compact ? 1 : 2}
                className="min-h-[44px] max-h-32 resize-none"
                data-testid={CONVERSATIONS.replyInput}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !(compact && isTouchDevice())) { e.preventDefault(); handleSend(); } }}
                onPaste={(e) => { const file = e.clipboardData?.files?.[0]; if (file) { e.preventDefault(); setPendingFile(file); } }}
              />
              {!pendingFile && !draft.trim() ? (
                <VoiceRecorder
                  onRecorded={handleVoice}
                  onError={(message) => toast({ title: "No se pudo grabar", description: message, variant: "destructive" })}
                  disabled={sending}
                />
              ) : (
                <Button onClick={handleSend} disabled={sending} className="bg-brand hover:bg-brand-hover text-white shrink-0" data-testid={CONVERSATIONS.replySendButton}>
                  <Send className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center" data-testid={CONVERSATIONS.replyDisabledNotice}>
            {isBot && chatAgentOn
              ? "El agente de IA esta atendiendo esta conversacion automaticamente."
              : isBot
                ? "El agente esta apagado en este chat. Toma control para responder."
                : "Esta conversacion esta resuelta. Toma control para responder o prende el agente en este chat."}
          </p>
        )}
      </div>
      <BookingLinkDialog open={bookingLinkOpen} onOpenChange={setBookingLinkOpen} conversationId={conversationId} onInsert={handleInsertBookingLink} />
      <TemplateDialog
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        title="Enviar plantilla"
        patientName={conversation.patient_name}
        language={conversation.language}
        preferred="retomar"
        onSend={onSendTemplate}
      />
    </div>
  );
}
